/**
 * NOTE: This requires `@sentry/nextjs` version 7.3.0 or higher.
 *
 * NOTE: If using this with `next` version 12.2.0 or lower, uncomment the
 * penultimate line in `CustomErrorComponent`.
 *
 * This page is loaded by Nextjs:
 *  - on the server, when data-fetching methods throw or reject
 *  - on the client, when `getInitialProps` throws or rejects
 *  - on the client, when a React lifecycle method throws or rejects, and it's
 *    caught by the built-in Nextjs error boundary
 *
 * See:
 *  - https://nextjs.org/docs/basic-features/data-fetching/overview
 *  - https://nextjs.org/docs/api-reference/data-fetching/get-initial-props
 *  - https://reactjs.org/docs/error-boundaries.html
 */

import * as Sentry from '@sentry/nextjs';
import NextErrorComponent from 'next/error';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { Box, Typography } from '@mui/material';

const CustomErrorComponent = (props: any) => {
  const router = useRouter();
  const { t } = useTranslation('common');
  const { t: tError } = useTranslation('error');

  const isFromHagoCom = router.query.from === 'hoiango.com';

  const { sx, statusCode } = props;

  // If you're using a Nextjs version prior to 12.2.1, uncomment this to
  // compensate for https://github.com/vercel/next.js/issues/8592
  // Sentry.captureUnderscoreErrorException(props);

  // Only show custom 404 for 404 errors
  if (statusCode === 404) {
    return (
      <Box sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#012F5B',
        ...sx
      }}>
        <Image src="/assets/images/ic_app_logo.png" alt="App Logo" width={200} height={80} style={{ margin: '0' }} />
        <Image src="/assets/images/image_not_found.png" alt="Not Found" width={400} height={400} />
        {isFromHagoCom ? (
          <>
            <Typography sx={theme => ({
              fontSize: 24,
              fontWeight: 700,
              color: theme.palette.primary.main,
              marginBottom: 2,
            })}> {tError('error.404.title')}</Typography>
            <Typography sx={theme => ({
              fontSize: 24,
              fontWeight: 700,
              color: theme.palette.white.main,
            })}> {tError('error.404.description')}</Typography>
          </>
        ) : <button
          onClick={() => router.push('/')}
          style={{
            marginTop: 24,
            padding: '12px 48px',
            fontSize: 18,
            background: '#e0a040',
            color: '#fff',
            border: 'none',
            borderRadius: 8,
            cursor: 'pointer',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
          }}
        >
          {t('back') || 'Back to Home'}
        </button>}
      </Box>
    );
  }

  return <NextErrorComponent statusCode={statusCode} />;
};

CustomErrorComponent.getInitialProps = async (contextData: any) => {
  // In case this is running in a serverless function, await this in order to give Sentry
  // time to send the error before the lambda exits
  await Sentry.captureUnderscoreErrorException(contextData);

  // This will contain the status code of the response
  return NextErrorComponent.getInitialProps(contextData);
};

export default CustomErrorComponent;
